import type {
  ExtendedProject,
  Project,
  ProjectClientRateStatus,
  SupabaseClient,
  Tables,
} from "@repo/supabase/database-types"
import { logger } from "@repo/utils/logger"
import type { DataTableBaseSearch } from "~/lib/schemas/common"
import {
  createInPredefinedListFilter,
  createListFilter,
  DataTableQueryBuilder,
} from "~/lib/utils/datatable-query-builder"
import {
  type AllProjectStatus,
  QUERYABLE_PROJECT_STATUSES,
} from "./projects.constants"
import type {
  CreateProjectClientRateData,
  CreateProjectData,
  UpdateProjectClientRateData,
  UpdateProjectData,
  UpdateProjectResourceRatesData,
} from "./projects.schema"

export type ProjectClientRate = Tables<"project_client_rates">

export const createProjectsService = (supabase: SupabaseClient) => {
  return new ProjectsService(supabase)
}

class ProjectsService {
  log = logger.child({
    module: "projects-service",
  })

  constructor(private readonly supabase: SupabaseClient) {}

  async create(data: CreateProjectData) {
    this.log.info("Creating project", { name: data.name })

    // Create the project
    // Note: client_unit_id, business_entity_id, and business_entity_unit_id
    // will be auto-populated by the trigger based on client_id
    // We provide temporary UUIDs that will be overridden by the trigger
    const { data: project, error: projectError } = await this.supabase
      .from("projects")
      .insert({
        client_id: data.clientId,
        client_unit_id: crypto.randomUUID(), // Temporary, will be overridden by trigger
        business_entity_id: crypto.randomUUID(), // Temporary, will be overridden by trigger
        business_entity_unit_id: crypto.randomUUID(), // Temporary, will be overridden by trigger
        name: data.name,
        description: data.description ?? null,
        status: data.status,
        start_date: data.startDate.toISOString(),
        end_date: data.endDate?.toISOString() ?? null,
        tags: data.tags ?? null,
      })
      .select()
      .single()
      .overrideTypes<Project, { merge: false }>()

    if (projectError) {
      this.log.error("Error creating project", { error: projectError })
      throw new Error("Failed to create project")
    }

    this.log.info("Project created successfully", { projectId: project.id })
    return project
  }

  async update(data: UpdateProjectData) {
    this.log.info("Updating project", { projectId: data.projectId })

    const { data: project, error: projectError } = await this.supabase
      .from("projects")
      .update({
        name: data.name,
        description: data.description ?? null,
        status: data.status,
        start_date: data.startDate.toISOString(),
        end_date: data.endDate?.toISOString() ?? null,
        tags: data.tags ?? null,
      })
      .eq("id", data.projectId)
      .select()
      .single()
      .overrideTypes<Project, { merge: false }>()

    if (projectError) {
      this.log.error("Error updating project", { error: projectError })
      throw new Error("Failed to update project")
    }

    this.log.info("Project updated successfully", { projectId: project.id })
    return project
  }

  async getOneExtended({ projectId }: { projectId: string }) {
    const { data, error } = await this.supabase
      .from("extended_projects")
      .select("*")
      .eq("id", projectId)
      .maybeSingle()

    if (error) {
      this.log.error("Error fetching extended project", { error })
      throw new Error(error.message)
    }

    if (!data) {
      this.log.error("Project not found", { projectId })
      throw new Error("Project not found")
    }

    return data
  }

  async getManyExtended(filters?: DataTableBaseSearch) {
    const builder = new DataTableQueryBuilder(this.supabase, {
      table: "extended_projects",
      searchField: "fts_project",
      defaultSort: { field: "created_at", ascending: false },
      filters: {
        status: createInPredefinedListFilter(
          "status",
          QUERYABLE_PROJECT_STATUSES,
        ),
        business_entity_unit_ids: createListFilter("business_entity_unit_id"),
        client_unit_ids: createListFilter("client_unit_id"),
      },
    })

    const { data, count } = await builder.execute<ExtendedProject>({
      page: 1,
      page_size: 20,
      ...filters,
    })

    return {
      projects: data,
      count,
    }
  }

  async setStatus({
    projectId,
    status,
  }: {
    projectId: string
    status: AllProjectStatus
  }) {
    const { data, error } = await this.supabase
      .from("projects")
      .update({ status })
      .eq("id", projectId)
      .select("id, status")
      .single()

    if (error) {
      this.log.error("Error updating project status", { error })
      throw new Error(error.message)
    }

    return data
  }

  async archive({ projectId }: { projectId: string }): Promise<void> {
    const { error } = await this.supabase.rpc("soft_delete_projects", {
      p_id: projectId,
    })

    if (error) {
      this.log.error("Error archiving project", { error })
      throw new Error(error.message)
    }

    this.log.info("Project archived successfully", { projectId })
  }

  async getClientRates({
    projectId,
    status,
  }: {
    projectId: string
    status?: ProjectClientRateStatus[]
  }) {
    let query = this.supabase
      .from("project_client_rates")
      .select("*")
      .eq("project_id", projectId)

    if (status && status.length > 0) {
      query = query.in("status", status)
    }

    const { data, error } = await query

    if (error) {
      this.log.error("Error fetching project client rates", { error })
      throw new Error(error.message)
    }

    return data
  }

  async createClientRates({
    projectId,
    clientRate,
  }: CreateProjectClientRateData) {
    const { data, error } = await this.supabase
      .from("project_client_rates")
      .insert({
        ...clientRate,
        project_id: projectId,
      })

    if (error) {
      this.log.error("Error creating project client rate", { error })
      throw new Error(error.message)
    }

    return data
  }

  async updateClientRate({
    projectClientRateId,
    clientRate,
  }: UpdateProjectClientRateData) {
    const { data, error } = await this.supabase
      .from("project_client_rates")
      .update(clientRate)
      .eq("id", projectClientRateId)

    if (error) {
      this.log.error("Error updating project client rate", { error })
      throw new Error(error.message)
    }

    return data
  }

  async getClientRateUsageCount({
    projectClientRateId,
  }: {
    projectClientRateId: string
  }) {
    const { count, error } = await this.supabase
      .from("project_resource_rates")
      .select("*", { count: "exact", head: true })
      .eq("project_client_rate_id", projectClientRateId)

    if (error) {
      this.log.error("Error fetching client rate usage count", { error })
      throw new Error(error.message)
    }

    return count ?? 0
  }

  async deleteClientRate({
    projectClientRateId,
  }: {
    projectClientRateId: string
  }) {
    const { error } = await this.supabase
      .from("project_client_rates")
      .delete()
      .eq("id", projectClientRateId)

    if (error) {
      this.log.error("Error deleting project client rate", { error })
      throw error
    }
  }

  async createResourceAssignment({
    projectId,
    resourceId,
    rates,
  }: {
    projectId: string
    resourceId: string
    rates: {
      project_client_rate_id: string
      rate: number
    }[]
  }) {
    const { data, error } = await this.supabase.rpc(
      "create_project_resource_assignment",
      {
        p_project_id: projectId,
        p_resource_id: resourceId,
        p_rates: rates,
      },
    )

    if (error) {
      this.log.error("Error creating project resource assignment", { error })
      throw new Error(error.message)
    }

    return data
  }

  async updateResourceRates({
    projectResourceId,
    rates,
  }: UpdateProjectResourceRatesData) {
    const { data, error } = await this.supabase.rpc(
      "update_project_resource_rates",
      {
        p_project_resource_id: projectResourceId,
        p_rates: rates,
      },
    )

    if (error) {
      this.log.error("Error updating project resource rates", { error })
      throw new Error(error.message)
    }

    return data
  }

  async deleteProjectResource({
    projectResourceId,
  }: {
    projectResourceId: string
  }) {
    const { error } = await this.supabase
      .from("project_resources")
      .delete()
      .eq("id", projectResourceId)

    if (error) {
      this.log.error("Error deleting project resource", { error })
      throw error
    }
  }
}
